import { calendarEventColors } from './colors';

/** Personal calendar event types (excludes synthetic lead/rights events). */
export type CalendarEventTypeId =
  | 'follow_up'
  | 'script'
  | 'shooting'
  | 'deadline'
  | 'meeting'
  | 'other';

export const CALENDAR_EVENT_TYPE_IDS: CalendarEventTypeId[] = [
  'follow_up',
  'script',
  'shooting',
  'deadline',
  'meeting',
  'other',
];

export const CALENDAR_EVENT_TYPE_I18N_KEYS: Record<CalendarEventTypeId, string> = {
  follow_up: 'calendar.followUp',
  script: 'calendar.script',
  shooting: 'calendar.shooting',
  deadline: 'calendar.deadline',
  meeting: 'calendar.meeting',
  other: 'calendar.other',
};

/** Default pastel hex per type (stored on events / in preferences). */
export const DEFAULT_EVENT_TYPE_COLORS: Record<CalendarEventTypeId, string> = {
  follow_up: calendarEventColors.blue,
  script: calendarEventColors.cyan,
  shooting: calendarEventColors.orange,
  deadline: calendarEventColors.red,
  meeting: calendarEventColors.purple,
  other: calendarEventColors.amber,
};

/**
 * Picker options: mid-tone `swatch` for UI (between pastel & text),
 * pastel `value` for calendar storage.
 */
export const CALENDAR_COLOR_PALETTE: { value: string; swatch: string; labelKey: string }[] = [
  { value: calendarEventColors.blue, swatch: '#3b82f6', labelKey: 'calendar.blue' }, // blue-500
  { value: calendarEventColors.cyan, swatch: '#06b6d4', labelKey: 'calendar.cyan' }, // cyan-500
  { value: calendarEventColors.emerald, swatch: '#10b981', labelKey: 'calendar.green' }, // emerald-500
  { value: calendarEventColors.amber, swatch: '#f59e0b', labelKey: 'calendar.yellow' }, // amber-500
  { value: calendarEventColors.orange, swatch: '#f97316', labelKey: 'calendar.orange' }, // orange-500
  { value: calendarEventColors.rose, swatch: '#f43f5e', labelKey: 'calendar.rose' }, // rose-500
  { value: calendarEventColors.red, swatch: '#ef4444', labelKey: 'calendar.red' }, // red-500
  { value: calendarEventColors.purple, swatch: '#a855f7', labelKey: 'calendar.purple' }, // purple-500
];

const PASTEL_SET = new Set(CALENDAR_COLOR_PALETTE.map((c) => c.value));

export function isCalendarPaletteColor(hex: unknown): hex is string {
  return typeof hex === 'string' && PASTEL_SET.has(hex);
}

export function swatchForPastel(pastel: string): string {
  return CALENDAR_COLOR_PALETTE.find((c) => c.value === pastel)?.swatch ?? pastel;
}

export function mergeEventTypeColors(
  overrides?: Partial<Record<CalendarEventTypeId, string>> | null
): Record<CalendarEventTypeId, string> {
  const merged = { ...DEFAULT_EVENT_TYPE_COLORS };
  if (!overrides) return merged;
  for (const id of CALENDAR_EVENT_TYPE_IDS) {
    const v = overrides[id];
    if (isCalendarPaletteColor(v)) merged[id] = v;
  }
  return merged;
}

export function parseEventTypeColorOverrides(
  raw: unknown
): Partial<Record<CalendarEventTypeId, string>> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Partial<Record<CalendarEventTypeId, string>> = {};
  for (const id of CALENDAR_EVENT_TYPE_IDS) {
    const v = (raw as Record<string, unknown>)[id];
    if (isCalendarPaletteColor(v)) out[id] = v;
  }
  return out;
}
